/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Popover,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  CcfTypography,
  CcfAvatar,
  CcfBadge,
  CcfWorkingIcon,
  CcfAvailableIcon,
  CcfUnavailableIcon,
  CcfOfflineIcon,
  CcfMessageGroupIcon,
  CcfBox,
  CcfOverflowIcon,
  CcfPlusIcon,
  useTranslator,
  CcfSearchIcon,
  CcfEditPenIcon,
  CcfMessageSupervisorIcon,
} from '@nice-ccf/ui-controls';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import {
  CONVERSATION_CONTAINER_HEADER_HEIGHT,
  CONVERSATION_CONTAINER_REPLYBOX_HEIGHT,
  CONVERSATION_CONTAINER_FOOTER_HEIGHT,
} from '../utils/conversations.utils';
import {
  ccfConversationsActions,
  getAgentMessagesAppSpaceView,
  getSelectedAgent,
  getSelectedAgentType,
} from '../+state/ccf-conversations.slice';
import React from 'react';
import { useMsal } from '@azure/msal-react';
import {
  ArrowBackIosNew,
  KeyboardArrowDown,
  KeyboardBackspace,
  Star,
} from '@mui/icons-material';

const useStyles = makeStyles((theme) =>
  createStyles({
    mainContainer: {
      height: `calc( 100% - ${CONVERSATION_CONTAINER_FOOTER_HEIGHT}px - ${CONVERSATION_CONTAINER_HEADER_HEIGHT}px -
        ${CONVERSATION_CONTAINER_REPLYBOX_HEIGHT}px )`,
      // height: '70%',
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    listItemRoot: {
      alignItems: 'flex-start',
      justifyContent: 'center',
      gap: 10,
      paddingInline: 8,
      '&:hover': {
        backgroundColor: `var(--popover-hover-color)`,
      },
    },
    listItemIcon: {
      minWidth: 36,
      marginTop: 8,
    },
    listItemTextPrimary: {
      display: 'flex',
      flexDirection: 'row',
      gap: 5,
      '& > span': {
        marginLeft: 6,
      },
    },
    listItemTextSecondary: {
      fontSize: 12,
      marginTop: 8,
    },
    startOfConversation: {
      textAlign: 'center',
      fontSize: '1rem',
      color: '#616c75',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-evenly',
      '& > hr': {
        flex: 1,
      },
    },
    badgeAvatarIcon: {
      width: 17,
      height: 17,
    },
    conversationContainerHeader: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px',
    },
    selectedAgentInfoBlock: {
      display: 'flex',
      flexDirection: 'row',
      gap: 5,
    },
    agentTitle: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '700',
      lineHeight: '16px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
    },
    agentSubTitle: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '16px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
      textTransform: 'capitalize',
    },
    timeStampTxt: {
      fontFamily: 'Open Sans',
      fontSize: '10px',
      fontWeight: '400',
      lineHeight: '13px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
      marginTop: '4px',
    },
    chatTxt: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '15px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#333333',
    },
    chatDate: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '15px',
      textAlign: 'left',
      textUnderlinePosition: 'from-font',
      textDecorationSkipInk: 'none',
      color: '#767676',
      margin: '0px 15px',
    },
    groupMembersPopoverContainer: {
      width: 'auto',
      maxHeight: '400px',
      overflowY: 'auto',
      boxSizing: 'border-box',
      background: '#ffffff',
      border: '1px solid #ebebeb',
      borderRadius: '4px',
      padding: '10px',
    },
    groupMemberBlock: {
      height: '30px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 10,
    },
    groupMemberName: {
      fontFamily: 'Open Sans',
      fontSize: '12px',
      fontWeight: '400',
      lineHeight: '15px',
      color: '#333333',
    },
    addMemberTxt: {
      fontFamily: 'Open Sans',
      fontSize: '14px',
      fontWeight: '700',
      lineHeight: '19px',
      color: '#003D7A',
    },
    kebabMenuContainer: {
      width: 'auto',
      boxSizing: 'border-box',
      background: '#ffffff',
      border: '1px solid #ebebeb',
      borderRadius: '4px',
      padding: '10px',
    },
    kebabeMenuBlock: {
      height: '32px',
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: 10,
    },
    menuIcon: {
      width: '18px',
      height: '18px',
    },
    menuName: {
      fontFamily: 'Open Sans',
      fontSize: '13px',
      fontWeight: '600',
      lineHeight: '22px',
      color: '#333333',
    },
    supervisorStatusIcon: {
      fontSize: '0.925em',
      marginRight: '0px',
    },
  })
);

/**
 * Component for conversation container
 * @example - <ConversationsContainer />
 * @returns
 */
export const ConversationsAppContainer = () => {
  const [translate] = useTranslator();
  const styles = useStyles();
  const dispatch = useDispatch();
  const QuickMessageContainerRef: any = useRef();
  const { accounts } = useMsal();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const selectedAgent = useSelector(getSelectedAgent);
  const selectedAgentType = useSelector(getSelectedAgentType);
  const agentMessagesAppSpaceView = useSelector(getAgentMessagesAppSpaceView);
  const { setAgentMessagesAppSpaceView } = ccfConversationsActions;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showGroupMembersPopover, setShowGroupMembersPopover] = useState(false);
  const [showKebabMenu, setshowKebabMenu] = useState(false);

  /**
   * Function to get selected agent messages
   * @param e - any
   * @example getSelectedAgentMessages()
   */
  const getSelectedAgentMessages = () => {
    return selectedAgent?.messages?.map((message: any) => {
      const isDirectOrFavourite =
        selectedAgentType === 'favourite' || selectedAgentType === 'direct';
      const isGroup = selectedAgentType === 'group';
      const isAnotherAgent = message.name === 'another agent';

      return {
        name:
          isDirectOrFavourite && isAnotherAgent
            ? selectedAgent.name
            : isGroup
            ? message.name
            : loginCredentialsFromSSO,
        status:
          isDirectOrFavourite && isAnotherAgent
            ? selectedAgent.status
            : isGroup
            ? message.status
            : 'available',
        text: message.text,
        timestamp: message.timestamp,
        color: message.color,
      };
    });
  };

  const selectedAgentMessages = getSelectedAgentMessages();

  useEffect(() => {
    setTimeout(() => {
      document.getElementById('messageBodyContainer')?.scroll({
        top: document.getElementById('messageBodyContainer')?.scrollHeight, //scroll to the bottom of the element
        behavior: 'smooth', //auto, smooth, initial, inherit
      });
    }, 500);
  }, [selectedAgent]);

  /**
   * Function to render status icon
   * @param e - any
   * @example renderStatusIcon(status)
   */
  function renderStatusIcon(status: string) {
    switch (status) {
      case 'working':
        return <CcfWorkingIcon className={styles.badgeAvatarIcon} />;
      case 'available':
        return <CcfAvailableIcon className={styles.badgeAvatarIcon} />;
      case 'busy':
        return <CcfUnavailableIcon className={styles.badgeAvatarIcon} />;
      case 'offline':
        return (
          <CcfOfflineIcon
            className={styles.badgeAvatarIcon}
            style={{ color: 'inherit' }}
          />
        );
      default:
        return <CcfAvailableIcon className={styles.badgeAvatarIcon} />;
    }
  }

  /**
   * Function to go back to agent list
   * @example backtoAgentList()
   */
  const backtoAgentList = () => {
    dispatch(setAgentMessagesAppSpaceView('agentsView'));
  };

  /**
   * Method to open group members popover
   * @example showGroupMembersPopoverHandler(event);
   */
  const showGroupMembersPopoverHandler = (event: any) => {
    setshowKebabMenu(false);
    setAnchorEl(event.currentTarget);
    setShowGroupMembersPopover(true);
  };

  /**
   * Method to open kebab menu popover
   * @example showKebabMenuHandler(event);
   */
  const showKebabMenuHandler = (event: any) => {
    setShowGroupMembersPopover(false);
    setAnchorEl(event.currentTarget);
    setshowKebabMenu(true);
  };

  /**
   * Method to close the group members popover
   * @example handleClose()
   */
  const handleClose = () => {
    setAnchorEl(null);
    setShowGroupMembersPopover(false);
  };

  /**
   * Method to close the kebab menu popover
   * @example handleClose()
   */
  const handleKebabMenuClose = () => {
    setAnchorEl(null);
    setshowKebabMenu(false);
  };

  return (
    <>
      <CcfBox className={styles.conversationContainerHeader}>
        {agentMessagesAppSpaceView === 'messagesView' && (
          <ArrowBackIosNew
            onClick={backtoAgentList}
            style={{ fontSize: '14px', cursor: 'pointer' }}
          />
        )}
        {selectedAgent &&
          Object.entries(selectedAgent).length > 0 &&
          (selectedAgentType === 'group' ? (
            <div className={styles.selectedAgentInfoBlock}>
              <CcfMessageGroupIcon />
              <CcfTypography className={styles.agentTitle}>
                {selectedAgent.name}
              </CcfTypography>
              <CcfTypography className={styles.agentSubTitle}>
                | {selectedAgent?.groupMembers.length} Members
              </CcfTypography>
              <KeyboardArrowDown
                onClick={(e) => {
                  e.stopPropagation();
                  showGroupMembersPopoverHandler(e);
                }}
                fontSize="small"
              />
            </div>
          ) : selectedAgent.name === 'Supervisor' ? (
            <div className={styles.selectedAgentInfoBlock}>
              <CcfMessageSupervisorIcon
                classes={{ root: styles.supervisorStatusIcon }}
              />
              <CcfTypography className={styles.agentTitle}>
                {`${translate(selectedAgent.name)}` !== 'undefined'
                  ? `${translate(selectedAgent.name) as any}`
                  : selectedAgent.name}
              </CcfTypography>
            </div>
          ) : (
            <div className={styles.selectedAgentInfoBlock}>
              {renderStatusIcon(selectedAgent.status)}
              <CcfTypography className={styles.agentTitle}>
                {selectedAgent.name}
              </CcfTypography>
              <CcfTypography className={styles.agentSubTitle}>
                | {selectedAgent.status}
              </CcfTypography>
            </div>
          ))}
        <CcfOverflowIcon
          onClick={(e) => {
            e.stopPropagation();
            showKebabMenuHandler(e);
          }}
        />
      </CcfBox>
      <Divider />
      <div
        className={styles.mainContainer}
        id="messageBodyContainer"
        ref={QuickMessageContainerRef}
      >
        <List>
          {selectedAgentMessages?.map(
            (
              chat: {
                name: string;
                status: string;
                text: string;
                timestamp: string;
                color: string;
              },
              index: number
            ) => (
              <React.Fragment key={`chat-message-${index}`}>
                {(index === 0 ||
                  new Date(chat.timestamp).toLocaleDateString() !==
                    new Date(
                      selectedAgentMessages[index - 1].timestamp
                    ).toLocaleDateString()) && (
                  <div className={styles.startOfConversation}>
                    <Divider />
                    <span className={styles.chatDate}>
                      {new Date(chat.timestamp).toLocaleDateString() ===
                      new Date().toLocaleDateString()
                        ? 'Today'
                        : new Date(chat.timestamp).toLocaleDateString('en-US', {
                            weekday: 'long',
                            month: 'short',
                            day: 'numeric',
                          })}
                    </span>
                    <Divider />
                  </div>
                )}
                <ListItem tabIndex={0} classes={{ root: styles.listItemRoot }}>
                  <ListItemIcon classes={{ root: styles.listItemIcon }}>
                    {chat.name === 'Supervisor' ? (
                      <CcfAvatar
                        width={25}
                        height={25}
                        fontSize={12}
                        bgcolor={chat.color}
                        borderColor={chat.color}
                        border={true}
                      >
                        S
                      </CcfAvatar>
                    ) : (
                      <CcfBadge
                        overlap="circular"
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        badgeContent={renderStatusIcon(chat.status)}
                      >
                        <CcfAvatar
                          width={25}
                          height={25}
                          fontSize={12}
                          bgcolor={chat.color}
                          borderColor={chat.color}
                          border={true}
                        >
                          {chat.name
                            .split(' ')
                            .map((n: string) => n[0])
                            .join('')}
                        </CcfAvatar>
                      </CcfBadge>
                    )}
                  </ListItemIcon>
                  <ListItemText
                    classes={{
                      primary: styles.listItemTextPrimary,
                      secondary: styles.listItemTextSecondary,
                    }}
                    primary={
                      <>
                        <CcfTypography className={styles.agentTitle}>
                          {chat.name}
                        </CcfTypography>
                        <CcfTypography className={styles.timeStampTxt}>
                          {new Date(chat.timestamp).toLocaleString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                          })}
                        </CcfTypography>
                      </>
                    }
                    secondary={
                      <CcfTypography className={styles.chatTxt}>
                        {chat.text}
                      </CcfTypography>
                    }
                  ></ListItemText>
                </ListItem>
              </React.Fragment>
            )
          )}
        </List>
      </div>
      {showGroupMembersPopover && (
        <Popover
          id={'group-members-popover'}
          open={showGroupMembersPopover}
          keepMounted
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          style={{ marginLeft: '-10px' }}
        >
          <CcfBox className={styles.groupMembersPopoverContainer}>
            {selectedAgent?.groupMembers?.map((member: any, index: number) => (
              <CcfBox key={index} className={styles.groupMemberBlock}>
                {renderStatusIcon(member.status)}
                <CcfTypography className={styles.groupMemberName}>
                  {member.name}
                </CcfTypography>
              </CcfBox>
            ))}
            <CcfBox className={styles.groupMemberBlock}>
              <CcfPlusIcon fillColor="#003D7A" fontSize="small" />
              <CcfTypography className={styles.addMemberTxt}>
                {translate('add_member')}
              </CcfTypography>
            </CcfBox>
          </CcfBox>
        </Popover>
      )}
      {showKebabMenu && (
        <Popover
          id={'kebaab-menu-popover'}
          open={showKebabMenu}
          keepMounted
          anchorEl={anchorEl}
          onClose={handleKebabMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <CcfBox className={styles.kebabMenuContainer}>
            {selectedAgentType === 'group' ? (
              <>
                <CcfBox className={styles.kebabeMenuBlock}>
                  <CcfSearchIcon className={styles.menuIcon} />
                  <CcfTypography className={styles.menuName}>
                    {translate('search_in_chat')}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.kebabeMenuBlock}>
                  <CcfPlusIcon className={styles.menuIcon} />
                  <CcfTypography className={styles.menuName}>
                    {translate('add_remove_member')}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.kebabeMenuBlock}>
                  <CcfEditPenIcon className={styles.menuIcon} />
                  <CcfTypography className={styles.menuName}>
                    {translate('edit_group_name')}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.kebabeMenuBlock}>
                  <KeyboardBackspace
                    style={{ color: '#E4263C' }}
                    className={styles.menuIcon}
                  />
                  <CcfTypography
                    style={{ color: '#E4263C' }}
                    className={styles.menuName}
                  >
                    {translate('leave_group')}
                  </CcfTypography>
                </CcfBox>
              </>
            ) : (
              <>
                <CcfBox className={styles.kebabeMenuBlock}>
                  <CcfSearchIcon className={styles.menuIcon} />
                  <CcfTypography className={styles.menuName}>
                    {translate('search_in_chat')}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.kebabeMenuBlock}>
                  {selectedAgent.isFavorite ? (
                    <Star
                      style={{
                        color: '#CC8605',
                      }}
                      className={styles.menuIcon}
                    />
                  ) : (
                    <StarOutlineOutlinedIcon
                      style={{ color: '#526B7A' }}
                      className={styles.menuIcon}
                    />
                  )}
                  <CcfTypography className={styles.menuName}>
                    {selectedAgent.isFavorite
                      ? translate('remove_from_favorites')
                      : translate('add_to_favorites')}
                  </CcfTypography>
                </CcfBox>
                <CcfBox className={styles.kebabeMenuBlock}>
                  <CcfPlusIcon className={styles.menuIcon} />
                  <CcfTypography className={styles.menuName}>
                    {translate('add_member')}
                  </CcfTypography>
                </CcfBox>
              </>
            )}
          </CcfBox>
        </Popover>
      )}
    </>
  );
};
